import React, { createContext, useEffect, useState, useRef } from "react";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { useLocation, useNavigate } from "react-router-dom";
import RouteConstants from "../../../constants/routeConstants.json";
import { planViewApi, deletePlanApi, planStatusApi } from "../../../api/Api";
import { useCookies } from "react-cookie";


export const PlanDetailsContext = createContext({});
export const PlanDetailsContextProvider = ({ children }) => {
  const [spinnerStatus, setSpinnerStatus] = useState(false);
  const [planStatus, setPlanStatus] = useState("A");
  const [cookies, setCookie, removeCookie] = useCookies(["user"]);
  const [changeStatus, setChangeStatus] = useState("");
  const [statusPopup, setStatusPopup] = useState(false);
  const [statusMessagePopup, setStatusMessagePopup] = useState(false);
  const [errorPopup, setErrorPopup] = useState(false);
  const [errorPopupMessage, setErrorPopupMessage] = useState("");
  const [successPopup, setSuccessPopup] = useState(false);
  const [buttonText, setButtonText] = useState("");
  const [statusValue, setStatusValue] = useState(0);
  const [planData, setPlanData] = useState("");
  const [successPopupMessage, setSuccessPopupMessage] = useState("");
  const [deleteAlert, setDeleteAlert] = useState(false);
  const [deactiveAlert, setDeactiveAlert] = useState(false);
  const [activeAlert, setActiveAlert] = useState(false);
  const [deleteErrorPopup, setDeleteErrorPopup] = useState(false);
  const [statusErrorPopup, setStatusErrorPopup] = useState(false);
  const [isExpansion, setIsExpansion] = useState(false);

  const pdfRef = useRef(); // Reference to the section to be converted to PDF

  const downloadPDF = () => {
    const section = pdfRef.current;

    // Use html2canvas to render the section into a canvas
    html2canvas(section, { scale: 2 }) // Higher scale improves quality
      .then((canvas) => {
        const imgData = canvas.toDataURL('image/png'); // Convert canvas to image data
        const pdf = new jsPDF('p', 'mm', 'a4'); // A4 size: 210 x 297 mm
        const pageWidth = 210;
        const pageHeight = 297;

        const imgWidth = pageWidth;
        const imgHeight = (canvas.height * pageWidth) / canvas.width;

        // Add the image to the PDF
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);

        // Save the PDF
        pdf.save('planDetail.pdf');
      })
      .catch((err) => console.error('Failed to generate PDF:', err));
  };

  const location = useLocation();
  const planId = location?.state?.id;
  const navigate = useNavigate();

  const permissionAccess = JSON.parse(
    localStorage.getItem("permissions")
  )?.filter((item) => {
    return item.feature === "67371d68534964294f80cfe3";
  })[0];

  const getPlanDetailApi = async (id) => {
    try {
      setSpinnerStatus(true);
      const getDetailData = await planViewApi(id);
      if (getDetailData.status === 200) {
        setSpinnerStatus(false);
        setErrorPopup(false);
        setPlanData(getDetailData.data.data.plan);
      }
    } catch (error) {
      const responseStatus = error?.response?.data?.statusCode;
      const errorMessage =
        error?.response?.data?.message || "Something went wrong...";

      setStatusValue(responseStatus);
      setErrorPopupMessage(errorMessage);
      setSpinnerStatus(false);
      setErrorPopup(true);

      if (responseStatus === 400) {
        setButtonText("Try Again");
      } else if (responseStatus === 401 || responseStatus === 403) {
        setButtonText("Login");
      } else {
        setButtonText("Try Again");
      }
    }
  };

  const onClickExpansion = () =>{
    setIsExpansion(true);
  }

  const deletePlanManage = async (id) => {
    try {
      setSpinnerStatus(true);
      const getPlan = await deletePlanApi(id);
      if (getPlan.data.statusCode === 200) {
        setDeleteAlert(false);
        setSuccessPopup(true);
        setSpinnerStatus(false);
        setStatusPopup(false);
        setErrorPopup(false);
        setSuccessPopupMessage(getPlan.data.message);
      }
    } catch (error) {
      const responseStatus = error?.response?.data?.statusCode;
      const errorMessage =
        error?.response?.data?.message || "Something went wrong...";

      setDeleteAlert(false);
      setStatusValue(responseStatus);
      setErrorPopupMessage(errorMessage);
      setErrorPopup(true);

      if (responseStatus === 400) {
        setButtonText("Try Again");
      } else if (responseStatus === 401 || responseStatus === 403) {
        setButtonText("Login");
      } else {
        setButtonText("Try Again");
      }
    }
    setSpinnerStatus(false);
  };

  const goToLogin = () => {
    removeCookie("x-access-token");
    navigate(RouteConstants.LOGIN);
  };

  const activePlanData = async (id, status) => {
    try {
      setSpinnerStatus(true);
      const getrole = await planStatusApi(id, status);
      if (getrole.data.statusCode === 200) {
        setActiveAlert(false);
        setSuccessPopup(true);
        setSpinnerStatus(false);
        setDeactiveAlert(false);
        setStatusPopup(false);
        setErrorPopup(false);
        setSuccessPopupMessage(getrole.data.message);
      }
    } catch (error) {
      const responseStatus = error?.response?.data?.statusCode;
      const errorMessage =
        error?.response?.data?.message || "Something went wrong...";

      setStatusValue(responseStatus);
      setErrorPopupMessage(errorMessage);
      setErrorPopup(true);

      if (responseStatus === 400) {
        setButtonText("Try Again");
      } else if (responseStatus === 401 || responseStatus === 403) {
        setButtonText("Login");
      } else {
        setButtonText("Try Again");
      }
    }
    setSpinnerStatus(false);
  };

  useEffect(() => {
    getPlanDetailApi(planId);
  }, []);

  const editPlanData = (id) => {
    navigate(RouteConstants.EDIT_PLAN, { state: { id } });
  };

  const planDetailData = (id) => {
    navigate(RouteConstants.PLAN_DETAILS);
  };

  const planListPage = () => {
    navigate(RouteConstants.LIST_PLAN);
  };

  const providerValue = {
    spinnerStatus,
    planId,
    planStatus,
    planData,
    getPlanDetailApi,
    editPlanData,
    permissionAccess,
    deletePlanManage,
    successPopup,
    successPopupMessage,
    setDeleteAlert,
    planListPage,
    setSuccessPopupMessage,
    deleteAlert,
    setSuccessPopup,
    errorPopup,
    errorPopupMessage,
    buttonText,
    errorPopupMessage,
    statusValue,
    setErrorPopup,
    planDetailData,
    deactiveAlert,
    setDeactiveAlert,
    activeAlert,
    setActiveAlert,
    activePlanData,
    changeStatus,
    setChangeStatus,
    goToLogin,
    deleteErrorPopup,
    setDeleteErrorPopup,
    statusErrorPopup,
    setStatusErrorPopup,
    downloadPDF,
    pdfRef,
    onClickExpansion,
    isExpansion,
    setIsExpansion
  };

  return (
    <PlanDetailsContext.Provider value={providerValue}>
      {children}
    </PlanDetailsContext.Provider>
  );
};
